import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from "react"
import { marked } from "marked"
import DOMPurify from "dompurify"

const EXAMPLES = [
  "The opening lead was made out of turn.",
  "A defender exposed two cards while drawing from their hand.",
  "North bid out of rotation during the auction.",
  "Declarer claimed the remaining tricks but a defender disputed it.",
]

// Preferred display order for the reference sections.
const SOURCE_ORDER = [
  "2017 Laws of Duplicate Bridge",
  "Duplicate Decisions (ACBL, 2020)",
]

// Remove a leading law-number prefix like "LAW 49 - " or "49 – " so titles
// don't repeat the number already shown next to them.
function cleanTitle(title) {
  return (title || "").replace(/^\s*(?:LAW\s+)?\d+\s*[-–—]\s*/i, "").trim()
}

// Group citations by their source document, ordered with the Laws first.
function groupSources(sources) {
  const groups = new Map()
  for (const s of sources) {
    if (!groups.has(s.source)) groups.set(s.source, [])
    groups.get(s.source).push(s)
  }
  const labels = [...groups.keys()].sort((a, b) => {
    const ia = SOURCE_ORDER.indexOf(a)
    const ib = SOURCE_ORDER.indexOf(b)
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib)
  })
  return labels.map(label => [
    label,
    [...groups.get(label)].sort((a, b) => (parseInt(a.law, 10) || 0) - (parseInt(b.law, 10) || 0)),
  ])
}

// Append newly cited sources, skipping any already listed for the same law/source.
function mergeSources(existing, extra) {
  const seen = new Set(existing.map(s => `${s.source}|${s.law}`))
  const additions = extra.filter(s => !seen.has(`${s.source}|${s.law}`))
  return additions.length ? [...existing, ...additions] : existing
}

// Build a single case-insensitive regex matching any defined term as a whole
// word, longest terms first so multi-word phrases win over their parts.
function buildTermRegex(terms) {
  if (!terms.length) return null
  const escaped = terms
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  return new RegExp(`\\b(?:${escaped.join("|")})\\b`, "gi")
}

// Wrap defined terms in sanitized HTML with a clickable span, operating only on
// text nodes so tags/attributes and existing links are never touched.
function annotateDefinitions(html, regex) {
  if (!regex || !html) return html

  const template = document.createElement("template")
  template.innerHTML = html

  const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT)
  const nodes = []
  while (walker.nextNode()) nodes.push(walker.currentNode)

  for (const node of nodes) {
    if (node.parentElement?.closest(".definition-term, a, code, pre")) continue

    const text = node.nodeValue
    regex.lastIndex = 0
    if (!regex.test(text)) continue

    regex.lastIndex = 0
    const fragment = document.createDocumentFragment()
    let last = 0
    let match
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) fragment.appendChild(document.createTextNode(text.slice(last, match.index)))
      const span = document.createElement("span")
      span.className = "definition-term"
      span.dataset.term = match[0]
      span.textContent = match[0]
      fragment.appendChild(span)
      last = match.index + match[0].length
    }
    if (last < text.length) fragment.appendChild(document.createTextNode(text.slice(last)))
    node.parentNode.replaceChild(fragment, node)
  }

  return template.innerHTML
}

export default function App() {
  const [question, setQuestion] = useState("")
  const [loading, setLoading]   = useState(false)
  const [messages, setMessages] = useState([])
  const [activeSource, setActiveSource] = useState(null)
  const [error, setError]       = useState(null)
  const [definitions, setDefinitions] = useState({})
  const [popover, setPopover]   = useState(null)
  const popoverRef = useRef(null)

  // Applies an update to the most recent (assistant) turn while it streams.
  function updateLastMessage(updater) {
    setMessages(prev => {
      if (prev.length === 0) return prev
      const next = prev.slice()
      next[next.length - 1] = updater(next[next.length - 1])
      return next
    })
  }

  async function handleSubmit(e) {
    e.preventDefault()
    const q = question.trim()
    if (!q || loading) return

    const history = [...messages, { role: "user", content: q }]
    setMessages([...history, { role: "assistant", content: "", sources: [], done: false }])
    setQuestion("")
    setActiveSource(null)
    setPopover(null)
    setLoading(true)
    setError(null)

    try {
      const res = await fetch("/api/rulings", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "text/event-stream" },
        body: JSON.stringify({ messages: history.map(({ role, content }) => ({ role, content })) }),
      })

      if (!res.ok || !res.body) {
        let message = "Request failed"
        try { message = (await res.json()).error || message } catch { /* non-JSON body */ }
        throw new Error(message)
      }

      const reader  = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ""

      while (true) {
        const { value, done: streamDone } = await reader.read()
        if (streamDone) break
        buffer += decoder.decode(value, { stream: true })

        let sep
        while ((sep = buffer.indexOf("\n\n")) !== -1) {
          handleEvent(buffer.slice(0, sep))
          buffer = buffer.slice(sep + 2)
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.")
      // Drop the empty assistant placeholder so a failed turn can be retried.
      setMessages(prev => {
        const last = prev[prev.length - 1]
        return last && last.role === "assistant" && !last.content ? prev.slice(0, -1) : prev
      })
    } finally {
      setLoading(false)
    }
  }

  function handleEvent(rawEvent) {
    let event = "message"
    let data  = ""
    for (const line of rawEvent.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim()
      else if (line.startsWith("data:")) data += line.slice(5).trim()
    }
    if (!data) return

    let parsed
    try { parsed = JSON.parse(data) } catch { return }

    if (event === "sources") updateLastMessage(m => ({ ...m, sources: parsed.sources || [] }))
    else if (event === "extra_sources") updateLastMessage(m => ({ ...m, sources: mergeSources(m.sources, parsed.sources || []) }))
    else if (event === "token") updateLastMessage(m => ({ ...m, content: m.content + (parsed.token || "") }))
    else if (event === "done") updateLastMessage(m => ({ ...m, done: true }))
    else if (event === "error") setError(parsed.error || "Something went wrong. Please try again.")
  }

  function handleExample(ex) {
    setQuestion(ex)
    setMessages([])
    setActiveSource(null)
    setPopover(null)
    setError(null)
  }

  function reset() {
    setMessages([])
    setActiveSource(null)
    setPopover(null)
    setError(null)
    setQuestion("")
  }

  const hasResult  = messages.length > 0

  const termRegex = useMemo(() => buildTermRegex(Object.keys(definitions)), [definitions])
  const defsLower = useMemo(() => {
    const map = {}
    for (const [term, definition] of Object.entries(definitions)) {
      map[term.toLowerCase()] = { term, definition }
    }
    return map
  }, [definitions])

  // Render a turn's markdown to sanitized HTML, annotating defined terms only
  // once the turn is complete (annotation walks the DOM and is wasted mid-stream).
  const renderMarkdown = useCallback((text, withDefinitions) => {
    if (!text) return ""
    const cleaned = text.replace(/^\s*#*\s*\*{0,2}\s*ruling\b\s*:?\s*\*{0,2}\s*\n*/i, "")
    const html = DOMPurify.sanitize(marked.parse(cleaned))
    return withDefinitions ? annotateDefinitions(html, termRegex) : html
  }, [termRegex])

  const modalHtml = useMemo(() => {
    if (!activeSource) return ""
    return annotateDefinitions(DOMPurify.sanitize(marked.parse(activeSource.content || "")), termRegex)
  }, [activeSource, termRegex])

  useEffect(() => {
    fetch("/api/definitions", { headers: { Accept: "application/json" } })
      .then(res => (res.ok ? res.json() : {}))
      .then(setDefinitions)
      .catch(() => { /* definitions are a progressive enhancement */ })
  }, [])

  useEffect(() => {
    if (!activeSource) return
    const onKey = e => { if (e.key === "Escape") setActiveSource(null) }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [activeSource])

  useEffect(() => {
    if (!popover) return
    const onDocClick = e => {
      if (e.target.closest(".definition-term") || e.target.closest(".definition-popover")) return
      setPopover(null)
    }
    const onKey = e => { if (e.key === "Escape") setPopover(null) }
    document.addEventListener("click", onDocClick)
    window.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("click", onDocClick)
      window.removeEventListener("keydown", onKey)
    }
  }, [popover])

  function handleDefinitionClick(e) {
    const el = e.target.closest(".definition-term")
    if (!el) return
    const entry = defsLower[el.dataset.term.toLowerCase()]
    if (!entry) return
    const rect = el.getBoundingClientRect()
    setPopover({
      term: entry.term,
      definition: entry.definition,
      anchorCenterX: rect.left + rect.width / 2,
      anchorTop: rect.top,
      anchorBottom: rect.bottom,
    })
  }

  // Position the popover after render so it can be measured, keeping it fully
  // within the viewport (it can otherwise overflow off narrow screens). Once
  // placed it stays fixed to the viewport, so scrolling moves the text behind it.
  useLayoutEffect(() => {
    const node = popoverRef.current
    if (!popover || !node) return
    const margin = 8
    const { offsetWidth: width, offsetHeight: height } = node
    const vw = window.innerWidth
    const vh = window.innerHeight

    let left = popover.anchorCenterX - width / 2
    left = Math.max(margin, Math.min(left, vw - width - margin))

    let top = popover.anchorBottom + 6
    if (top + height > vh - margin) {
      const above = popover.anchorTop - height - 6
      top = above >= margin ? above : Math.max(margin, vh - height - margin)
    }

    node.style.left = `${left}px`
    node.style.top = `${top}px`
  }, [popover])

  return (
    <div className="page">
      <header className="site-header-container">
        <div className='site-header'>
          <div className='header-owl'>

            <img src="/owl.png" alt="Bridge Director's Ruling Assistant"  />
          </div>
          <div className='header-title'>
            Bridge&nbsp;Director&apos;s Ruling&nbsp;Assistant
          </div>
          <div className="disclaimer">
            <strong>Note:</strong> This tool helps directors find applicable laws and commentary.
            It is not a substitute for a qualified director and does not replace your own judgment.
            </div>
        </div>
      </header>

      <main className="main">
        {hasResult && (
          <div className="conversation">
            {messages.map((m, idx) => (
              m.role === "user" ? (
                <div key={idx} className="question-block">
                  <p className="question-text">{m.content}</p>
                </div>
              ) : (
                <div key={idx} className="result-box">
                  {m.content && (
                    <div
                      className="ruling-text"
                      onClick={handleDefinitionClick}
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content, m.done) }}
                    />
                  )}

                  {loading && !m.done && idx === messages.length - 1 && (
                    <div className="streaming-indicator" role="status" aria-live="polite" aria-label="Thinking">
                      <span className="dot" />
                      <span className="dot" />
                      <span className="dot" />
                    </div>
                  )}

                  {m.sources.length > 0 && (
                    <div className="sources">
                      <h2 className="sources-heading">Laws referenced</h2>
                      {groupSources(m.sources).map(([label, items]) => (
                        <div key={label} className="source-group">
                          <h3 className="source-group-heading">{label}</h3>
                          <ul className="sources-list">
                            {items.map((s, i) => (
                              <li key={i} className="source-item">
                                <button
                                  type="button"
                                  className="source-link"
                                  onClick={() => setActiveSource(s)}
                                  title="View the relevant passage"
                                >
                                  {s.law && <span className="law-number">Law {s.law}</span>}
                                  {cleanTitle(s.title) && <span className="law-title"> — {cleanTitle(s.title)}</span>}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="ruling-form">
          <label htmlFor="question" className="form-label">
            {hasResult ? "Add details or ask a follow-up:" : "Describe the situation:"}
          </label>
          <textarea
            id="question"
            className="form-textarea"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="e.g. Declarer played a card from the wrong hand after dummy was faced."
            rows={4}
            disabled={loading}
          />
          <button
            type="submit"
            className="submit-btn"
            disabled={loading || !question.trim()}
          >
            {loading ? "Searching laws…" : hasResult ? "Send" : "Find relevant laws"}
          </button>
        </form>

        {hasResult && !loading && (
          <button className="new-question-btn" onClick={reset}>
            Start a new question
          </button>
        )}

        {!hasResult && !loading && !error && (
          <div className="examples">
            <p className="examples-label">Try an example:</p>
            <div className="example-list">
              {EXAMPLES.map(ex => (
                <button
                  key={ex}
                  className="example-btn"
                  onClick={() => handleExample(ex)}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}

        <p className='public-sources'>
          Based on the publicly available<br />
          <a href='https://www.worldbridge.org/regulations/2017-laws-of-duplicate-bridge/'>2017 Laws of Duplicate Bridge</a> (World Bridge Federation)<br /> and{" "}
          <a href='https://web2.acbl.org/documentLibrary/rulings/DD2020.pdf'>Duplicate Decisions</a> (ACBL, 2020).
        </p>
      </main>

      <footer className="site-footer">
        <div className='promotion'>

          <p>
            This assistant is provided free by another great tool for bridge directors:{" "}
          </p>

          <div className='rsvp'>
            <div><a href="https://rsvpbridge.com" target="_blank" rel="noopener noreferrer"><img src="/rsvp-bridge-sm.png" alt="RSVP Bridge" /></a></div>
            <div><a href="https://rsvpbridge.com" target="_blank" rel="noopener noreferrer">www.rsvpbridge.com</a></div>
          </div>

        </div>
      </footer>

      {activeSource && (
        <div
          className="source-modal-overlay"
          onClick={() => setActiveSource(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Referenced law passage"
        >
          <div className="source-modal" onClick={e => e.stopPropagation()}>
            <button
              type="button"
              className="source-modal-close"
              onClick={() => setActiveSource(null)}
              aria-label="Close"
            >
              ×
            </button>
            <h3 className="source-modal-title">
              {activeSource.law && <span className="law-number">Law {activeSource.law}</span>}
              {cleanTitle(activeSource.title) && <span className="law-title"> — {cleanTitle(activeSource.title)}</span>}
            </h3>
            <p className="source-modal-doc">{activeSource.source}</p>
            <div
              className="source-modal-body"
              onClick={handleDefinitionClick}
              dangerouslySetInnerHTML={{ __html: modalHtml }}
            />
          </div>
        </div>
      )}

      {popover && (
        <div
          ref={popoverRef}
          className="definition-popover"
          role="tooltip"
        >
          <span className="definition-popover-term">{popover.term}</span>
          <span className="definition-popover-text">{popover.definition}</span>
        </div>
      )}
    </div>
  )
}
